import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams, useLocation } from "react-router-dom";
import GiftDetailsLayout from "../components/GiftDetailsLayout";
import { db } from "../api/firebase";
import { getGiftCardSummaryById } from "../services/giftCardSummaryService";
import { mergeSummaryWithVisualPreset } from "../constants/ecardVisualPresets";
import { buildPredefinedDemoGift, getVariantName } from "../constants/predefinedEcardDemos";
import { variantIndexFromGiftId } from "../utils/ecardGallery";
import "../styles/productDetails.css";
import "../styles/journey.css";

export default function ProductLanding() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();

  const occasionFromQuery = searchParams.get("occasion") || "";
  const occasionFromNav = location.state?.occasionSlug || "";
  const occasion = occasionFromQuery || occasionFromNav || "egift";

  const [product, setProduct] = useState(null);
  const [loadErr, setLoadErr] = useState(null);

  // Scaled-down preview: measure the scene container and set CSS scale
  const sceneRef = useRef(null);
  const [previewScale, setPreviewScale] = useState(0.4);
  const [isFullscreenOpen, setIsFullscreenOpen] = useState(false);
  const INNER_SIZE = 900; // inner design renders at 900×900 px

  const updateScale = useCallback(() => {
    if (sceneRef.current) {
      setPreviewScale(sceneRef.current.offsetWidth / INNER_SIZE);
    }
  }, []);

  useEffect(() => {
    updateScale();
    const ro = new ResizeObserver(updateScale);
    if (sceneRef.current) ro.observe(sceneRef.current);
    return () => ro.disconnect();
  }, [updateScale]);

  useEffect(() => {
    if (!db) {
      setLoadErr("Service is temporarily unavailable. Please try again shortly.");
      setProduct(null);
      return;
    }
    let cancelled = false;
    setLoadErr(null);
    setProduct(null);
    (async () => {
      const summary = await getGiftCardSummaryById(id);
      if (cancelled) return;
      if (!summary) {
        setLoadErr("This gift card was not found in your catalog.");
        return;
      }
      const merged = mergeSummaryWithVisualPreset(summary, occasion);
      setProduct(merged);
    })();
    return () => {
      cancelled = true;
    };
  }, [id, occasion]);

  // Which variant is selected (defaults to one derived from the gift id)
  const defaultVariant = useMemo(() => variantIndexFromGiftId(id), [id]);
  const [activeVariant, setActiveVariant] = useState(defaultVariant);

  // Reset when the gift id changes (e.g. navigating between products)
  useEffect(() => {
    setActiveVariant(variantIndexFromGiftId(id));
  }, [id]);

  const preview = useMemo(
    () => buildPredefinedDemoGift(occasion, activeVariant),
    [occasion, activeVariant]
  );

  if (loadErr) {
    return (
      <div className="pd-wrap">
        <div className="pd-error journey-panel">{loadErr}</div>
        <Link to="/" className="journey-link-back">
          Back home
        </Link>
      </div>
    );
  }

  if (!product) {
    return <p className="pd-loading">Loading design…</p>;
  }

  const finalPrice =
    product.Price - (product.Price * (product.Discount || 0)) / 100;

  const goCustomize = () => {
    const p = new URLSearchParams();
    if (occasion) p.set("occasion", occasion);
    navigate(`/product/${id}/customize?${p.toString()}`);
  };

  const goSchedule = () => {
    const p = new URLSearchParams();
    if (occasion) p.set("occasion", occasion);
    p.set("mode", "schedule");
    navigate(`/product/${id}/customize?${p.toString()}`);
  };

  const nextVariant = () => setActiveVariant((v) => (v + 1) % 4);
  const prevVariant = () => setActiveVariant((v) => (v + 3) % 4);

  return (
    <div className="pd-wrap journey-landing">
      <div className="pd-card journey-landing-card">
        <div className="pd-visual">
          {product.Discount > 0 && (
            <span className="pd-discount">{product.Discount}% off</span>
          )}
          {/* Live scaled-down preview of the actual GiftDetailsLayout */}
          <div className="pd-visual-ring">
            <div
              ref={sceneRef}
              className="pd-ecard-mini-scene"
              aria-label={`${getVariantName(occasion, activeVariant)} — design preview`}
            >
              <button
                type="button"
                className="pd-preview-arrow pd-preview-arrow--left"
                onClick={prevVariant}
                aria-label="Previous preview"
              >
                ←
              </button>
              <button
                type="button"
                className="pd-preview-arrow pd-preview-arrow--right"
                onClick={nextVariant}
                aria-label="Next preview"
              >
                →
              </button>
              <div
                key={activeVariant}
                className="pd-ecard-mini-inner"
                style={{ transform: `translate(-50%, -50%) scale(${previewScale})` }}
                aria-hidden
              >
                <GiftDetailsLayout
                  gift={{
                    ...preview,
                    Image1: product.imageurl || preview.Image1,
                  }}
                  categoryId={occasion}
                  variant={activeVariant}
                  isMini
                />
              </div>
            </div>
          </div>

          <div className="pd-sample-links">
            <p className="pd-sample-label">Choose a design:</p>
            <div className="pd-sample-links-row">
              {[0, 1, 2, 3].map((v) => (
                <button
                  key={v}
                  type="button"
                  className={`pd-sample-link-chip${activeVariant === v ? " pd-sample-link-chip--active" : ""}`}
                  onClick={() => setActiveVariant(v)}
                  aria-pressed={activeVariant === v}
                >
                  {getVariantName(occasion, v)}
                </button>
              ))}
            </div>
            <Link
              className="pd-sample-fullscreen-link"
              to="#"
              onClick={(e) => {
                e.preventDefault();
                setIsFullscreenOpen(true);
              }}
            >
              Open full-screen
            </Link>
          </div>
        </div>

        <div className="pd-body">
          <h1>{product.Tittle}</h1>
          <p>{product.Description}</p>

          <div className="pd-price-row">
            {product.Discount > 0 && (
              <span className="pd-old">₹{product.Price}</span>
            )}
            <span className="pd-new">From ₹{Math.round(finalPrice)}</span>
          </div>

          {product.Validity ? (
            <p className="pd-hint" style={{ marginTop: "0.5rem" }}>
              E-card link validity: <strong>{product.Validity}</strong> (how long the shared link
              stays active).
            </p>
          ) : null}

          <div className="pd-actions">
            <button type="button" className="pd-btn pd-btn-pay" onClick={goCustomize}>
              Create E-Card
            </button>
            <button type="button" className="pd-btn pd-btn-schedule" onClick={goSchedule}>
              Schedule for special occasion
            </button>
          </div>

          <p className="pd-hint">
            You will add names and your message, then review pricing (including any coupon) before
            paying securely with Razorpay.
          </p>

          <Link to="/" className="journey-link-muted">
            ← Back to occasions
          </Link>
        </div>
      </div>
      {isFullscreenOpen ? (
        <div className="pd-preview-overlay" role="dialog" aria-modal="true" aria-label="Full-screen preview">
          <button type="button" className="pd-preview-close" onClick={() => setIsFullscreenOpen(false)}>
            Close
          </button>
          <button
            type="button"
            className="pd-preview-arrow pd-preview-arrow--left pd-preview-arrow--fullscreen"
            onClick={prevVariant}
            aria-label="Previous preview"
          >
            ←
          </button>
          <button
            type="button"
            className="pd-preview-arrow pd-preview-arrow--right pd-preview-arrow--fullscreen"
            onClick={nextVariant}
            aria-label="Next preview"
          >
            →
          </button>
          <GiftDetailsLayout
            gift={{
              ...preview,
              Image1: product.imageurl || preview.Image1,
            }}
            categoryId={occasion}
            variant={activeVariant}
          />
        </div>
      ) : null}
    </div>
  );
}
